import React from "react";
import FormWithScanner from "./FormWithScanner";
import {EElementType, elementsNameKey} from "../../../models/utils/EElementType";
import {AElement} from "../../../models/elements/AElement";
import IdentificationExplanation from "../../utils/alert/explanation/IdentificationExplanation";
import ValidationButton from "../../utils/buttons/ValidationButton";
import ElementService from "../../../services/ElementService";
import Error from "../../utils/alert/error/Error";
import '../Step.css';
import {WithTranslation, withTranslation} from "react-i18next";

/**
* Interface inheriting the translation module.
* We put all the props of the component there.
**/
interface Props extends WithTranslation {
    /**
     * Type of the element to be identified
     */
    elementType: EElementType,
    /**
     * Function called when the user clicks on the validate button.
     * @param {AElement} element Element identified.
     */
    onValidate: (element: AElement) => void,
    /**
     * Initial element. Can be used in case of an update or when the element has already been identified.
     */
    initialElement?: AElement,
    /**
     * Give details of the stage. Will be displayed in brackets after the title.
     */
    detailTitle?: string
}

/**
 * Component allowing to identify an element and to validate.
 * @author Alizée Grand
 * @version 1.2
 * @creation 28/03/2022
 * @last_update 19/07/2022
 **/
class IdentificationStep extends React.Component<Props, {
    /**
     * Current element.
     */
    element: AElement,
    /**
     * Whether an error is displayed in the component or not.
     */
    isError: boolean
}> {

    /**
     * Constructor of the component.
     * Initializes its state by creating an element of the right type if there is not one already.
     * @param props
     */
    constructor(props: any) {
        super(props);
        let element = this.props.initialElement;
        if(element === undefined) {
           element = ElementService.createsElementAccordingToType(this.props.elementType);
        }
        this.state = {element: element, isError: false};
    }

    render() {

        /**
        * Retrieves from the props the function to access the translation.
        **/
        const getTranslation = this.props.t;

        /**
         * French name of the element being identified.
         */
        const elementTitle = getTranslation(elementsNameKey[this.props.elementType]).toLowerCase();

        /**
         * French title of the form allowing identification.
         */
        const title = getTranslation("steps.identification.element-title") + " " + elementTitle;

        const getTitle = () => {
            let stepTitle = title;
            if(this.props.detailTitle !== undefined) {
                stepTitle += " (" + getTranslation(this.props.detailTitle) + ")";
            }
            return stepTitle;
        }

        /**
         * Function called when the user clicks on the validate button.
         * Calls the eponymous function of the props if the element is correct, otherwise displays an error.
         */
        const onValidate = () => {
            if(this.state.element?.isCorrect()) {
                this.props.onValidate(this.state.element);
            } else {
                this.setState({isError: true});
            }
        }

        /**
         * Modifies the element stored in the local state of the component.
         * @param {AElement} element New element.
         */
        const setElement = (element: AElement) => {
            this.setState({element: element});
        }

        return (

            <div className="step-main-container">
                <IdentificationExplanation elementTitle={elementTitle}/>
                {
                    this.state.isError ? (
                        <Error>
                            {getTranslation("steps.identification.error")}
                        </Error>
                    ) : ""
                }
                <div className="form-container">
                    <FormWithScanner title={getTitle()} initialElement={this.state.element} setElement={setElement}/>
                </div>
                <div className="validation-container">
                    <ValidationButton onClick={onValidate}/>
                </div>
            </div>

        );

    };

}

export default withTranslation()(IdentificationStep);
